import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';

type SlideNav = 'spa' | 'page' | 'external';

interface HeroSlide {
  id: string;
  kicker: string;
  headline: string;
  subhead: string;
  cta: string;
  href: string;
  image: string;
  imageAlt: string;
  imagePosition: string;
  theme: 'club' | 'grow' | 'library';
  nav: SlideNav;
}

const SLIDES: HeroSlide[] = [
  {
    id: 'fall-garden-club',
    kicker: 'Fall Garden Club',
    headline: 'Frost is coming. That’s your cue.',
    subhead:
      'I’ll walk you through exactly what to plant, when to plant it, and how to keep it growing — based on your frost date.',
    cta: 'Join now',
    href: '/join',
    image: '/join/hero.png',
    imageAlt: 'Fresh carrots with leafy tops on a pink and lime background',
    imagePosition: 'object-center',
    theme: 'club',
    nav: 'page',
  },
  {
    id: 'grow-boxes',
    kicker: 'Grow Boxes',
    headline: 'Grow food at home. Without making your life harder.',
    subhead:
      'Blooma makes growing food simple with a reusable grow system and step-by-step guidance that fits into real life.',
    cta: 'Shop Grow Boxes',
    href: 'https://shop.bloomagrows.com/collections/all',
    image: '/home/blooma-grow-bag-human.jpg',
    imageAlt: 'A Blooma grow bag of basil and parsley on a kitchen counter',
    imagePosition: 'object-center',
    theme: 'grow',
    nav: 'external',
  },
  {
    id: 'resources',
    kicker: 'Resource library',
    headline: 'Guides, calendars, and plans — without the guesswork.',
    subhead:
      'Browse Blooma’s library of resources built for real life, so you know what to plant even if you’ve never grown food before.',
    cta: 'Browse resources',
    href: '/shop',
    image: '/home/resources-harvest.jpg',
    imageAlt: 'Baskets of freshly harvested carrots, cucumbers, and peppers',
    imagePosition: 'object-center',
    theme: 'library',
    nav: 'spa',
  },
];

const THEMES = {
  club: {
    panel: 'bg-[#ff4f8b] text-white',
    kicker: 'text-white/85',
    subhead: 'text-white/90',
    cta: 'bg-[#9CCC57] text-[#243a2e] hover:bg-[#7eae3d] hover:text-white',
  },
  grow: {
    panel: 'bg-[#FDFFF7] text-mulch',
    kicker: 'text-primary-700',
    subhead: 'text-mulch/80',
    cta: 'bg-primary-500 text-white hover:bg-primary-600',
  },
  library: {
    panel: 'bg-mulch text-cotton',
    kicker: 'text-primary-300',
    subhead: 'text-cotton/85',
    cta: 'bg-primary-500 text-[#243a2e] hover:bg-primary-600 hover:text-white',
  },
} as const;

function SlideLink({
  slide,
  className,
  children,
}: {
  slide: HeroSlide;
  className?: string;
  children: React.ReactNode;
}) {
  const shared = {
    className,
    'aria-label': `${slide.cta}. ${slide.headline}`,
  };

  if (slide.nav === 'spa') {
    return (
      <Link to={slide.href} {...shared}>
        {children}
      </Link>
    );
  }

  return (
    <a
      href={slide.href}
      {...shared}
      {...(slide.nav === 'external'
        ? { target: '_blank', rel: 'noopener noreferrer' }
        : {})}
    >
      {children}
    </a>
  );
}

export default function HomeHeroCarousel() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);

  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const syncMotion = () => setIsAutoPlaying(!media.matches);
    syncMotion();
    media.addEventListener('change', syncMotion);
    return () => media.removeEventListener('change', syncMotion);
  }, []);

  useEffect(() => {
    if (!isAutoPlaying) return undefined;
    const id = window.setInterval(() => {
      setCurrentIndex((index) => (index + 1) % SLIDES.length);
    }, 6500);
    return () => window.clearInterval(id);
  }, [isAutoPlaying, currentIndex]);

  const goToPrevious = useCallback(() => {
    setCurrentIndex((index) => (index - 1 + SLIDES.length) % SLIDES.length);
  }, []);

  const goToNext = useCallback(() => {
    setCurrentIndex((index) => (index + 1) % SLIDES.length);
  }, []);

  const goToIndex = useCallback((index: number) => {
    setCurrentIndex(index);
  }, []);

  const handleTouchStart = (event: React.TouchEvent) => {
    setTouchStart(event.targetTouches[0].clientX);
    setTouchEnd(event.targetTouches[0].clientX);
  };

  const handleTouchMove = (event: React.TouchEvent) => {
    setTouchEnd(event.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    const distance = touchStart - touchEnd;
    if (distance > 50) goToNext();
    if (distance < -50) goToPrevious();
    setTouchStart(0);
    setTouchEnd(0);
  };

  return (
    <section
      className="relative w-full overflow-hidden"
      aria-roledescription="carousel"
      aria-label="Featured offers"
      onMouseEnter={() => setIsAutoPlaying(false)}
      onMouseLeave={() => {
        if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          setIsAutoPlaying(true);
        }
      }}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <div className="relative min-h-[36rem] md:min-h-[32rem] lg:min-h-[38rem]">
        {SLIDES.map((slide, index) => {
          const theme = THEMES[slide.theme];
          const isActive = index === currentIndex;

          return (
            <div
              key={slide.id}
              className={`absolute inset-0 transition-opacity duration-500 ${
                isActive ? 'opacity-100 z-10' : 'opacity-0 z-0 pointer-events-none'
              }`}
              aria-hidden={!isActive}
            >
              <SlideLink
                slide={slide}
                className={`grid h-full md:grid-cols-[minmax(0,1fr)_minmax(0,1.55fr)] ${theme.panel}`}
              >
                <div className="order-2 flex flex-col justify-center px-6 py-8 md:order-1 md:px-10 lg:px-16">
                  <p
                    className={`mb-3 text-xs font-bold uppercase tracking-[0.18em] ${theme.kicker}`}
                  >
                    {slide.kicker}
                  </p>
                  <h1 className="font-dm-serif text-3xl leading-tight md:text-4xl lg:text-5xl">
                    {slide.headline}
                  </h1>
                  <p className={`mt-4 max-w-md text-base md:text-lg ${theme.subhead}`}>
                    {slide.subhead}
                  </p>
                  <span
                    className={`mt-6 inline-flex w-fit items-center rounded-md px-6 py-3 text-base font-bold transition-colors ${theme.cta}`}
                  >
                    {slide.cta}
                  </span>
                </div>
                <div className="order-1 h-72 overflow-hidden md:order-2 md:h-full">
                  <img
                    src={slide.image}
                    alt={slide.imageAlt}
                    className={`h-full w-full object-cover ${slide.imagePosition}`}
                    loading={index === 0 ? 'eager' : 'lazy'}
                  />
                </div>
              </SlideLink>
            </div>
          );
        })}
      </div>

      <button
        type="button"
        onClick={goToPrevious}
        className="absolute left-3 top-1/2 z-20 -translate-y-1/2 rounded-full bg-white/90 p-2 text-mulch shadow-md transition hover:bg-white md:left-4"
        aria-label="Previous slide"
      >
        <ChevronLeft className="h-6 w-6" />
      </button>
      <button
        type="button"
        onClick={goToNext}
        className="absolute right-3 top-1/2 z-20 -translate-y-1/2 rounded-full bg-white/90 p-2 text-mulch shadow-md transition hover:bg-white md:right-4"
        aria-label="Next slide"
      >
        <ChevronRight className="h-6 w-6" />
      </button>

      <div className="absolute bottom-4 left-1/2 z-20 flex -translate-x-1/2 gap-2 rounded-full bg-black/30 px-3 py-2 backdrop-blur-sm">
        {SLIDES.map((slide, index) => (
          <button
            key={slide.id}
            type="button"
            onClick={() => goToIndex(index)}
            className={`h-2.5 rounded-full transition-all ${
              index === currentIndex ? 'w-8 bg-white' : 'w-2.5 bg-white/55 hover:bg-white/80'
            }`}
            aria-label={`Go to ${slide.kicker}`}
            aria-current={index === currentIndex ? 'true' : undefined}
          />
        ))}
      </div>
    </section>
  );
}
